import React, { useEffect, useRef, useState, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQueries } from 'react-query';
import { string, bool } from 'prop-types';
import { styled } from '@nextiva/next-ui-components';
import {
  VoicemailVisualizer,
  GreetingVisualizer,
} from '@nextiva/voice-settings-module__voicemail-visualizer';
import getVoicemailErrorMessage from 'utils/getVoicemailErrorMessage';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import SectionSaveCancel from 'components/VoicemailSectionSaveCancel';
import { Skeleton } from 'components/Skeleton';
import NotifyModal from 'components/NotifyModal';
import Switch from 'components/VoicemailSwitch';
import RevealCard from 'components/VoicemailRevealCard';
import { DIALOG_KINDS } from 'constants/dialogKinds';
import { useSnackbar } from 'hooks';
import { HuntGroupVoicemailService } from 'services';
import { pxToRem } from 'utils/pxToRemConverter';
import '@nextiva/voice-settings-module__voicemail-visualizer/dist/styles.css';
import 'styles/application/_modules/_m-voice-settings__voicemail-visualizer.scss';
import 'styles/application/_modules/_m-voice-settings__voicemail-visualizer-new.scss';

import {
  setAdvancedIsLoading,
  setAdvancedResponse,
  setBusyGreetingOptions,
  setStorageType,
  setTransferOnZero,
  clearBusyGreetingOptions,
} from 'containers/VoiceMail/reducers/advanced';
import {
  setOptionsIsLoading,
  setOptionsResponse,
  setIsVoiceMailActive,
  setAnnouncements,
  setAssignedAnnouncement,
  setDefaultAnnouncement,
  setGreetingToRemove,
  setPinNumber,
} from 'containers/VoiceMail/reducers/options';
import sassVars from 'styles/application/_base/_variables.scss';
import AdvancedForm from './AdvancedForm';
import OptionsForm from './OptionsForm';

const {
  getHuntGroupVoicemailOptions,
  putHuntGroupVoicemailOptions,
  putHuntGroupVoicemailGreeting,
  postHuntGroupVoicemailGreeting,
  deleteHuntGroupVoicemailGreeting,
  getHuntGroupVoicemailAdvanced,
  putHuntGroupVoicemailAdvanced,
} = HuntGroupVoicemailService;

const Container = styled('div')`
  padding: 0 0 72px;
  max-width: 620px;
`;

const Header = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
  width: 100%;
  font-size: ${pxToRem('20px')};
  font-weight: 600;
`;

const Divider = styled('div')`
  border-bottom: 1px solid ${sassVars.dividerColor};
  margin-bottom: 10px;
`;

const Info = styled('div')`
  padding: 16px 20px;
  margin: 16px 0;
  font-size: ${pxToRem('14px')};
  background-color: ${sassVars.infoBG};
  line-height: 20px;
  letter-spacing: 0.25px;
`;

const SUCCESS_SNACKBAR = {
  message: 'Saved. Settings have been updated successfully.',
  severity: 'success',
};

const defaultGreeting = {
  greetingName: 'Default Greeting',
  greetingType: 'None',
};

const Voicemail = ({ huntGroupId = '', isHipaaEnabled = false }) => {
  const dispatch = useDispatch();
  const [extension, setExtension] = useState('');
  const [
    {
      data: voiceMailOptions,
      isLoading: voiceMailOptionsLoading,
      isError: voiceMailOptionsError,
      remove: removeVoiceMailOptions,
    },
    {
      data: voiceMailAdvanced,
      isLoading: voiceMailAdvancedLoading,
      isError: voiceMailAdvancedError,
      remove: removeVoiceMailAdvanced,
    },
  ] = useQueries([
    {
      queryKey: ['huntGroupVoicemailOptions', huntGroupId],
      queryFn: () => getHuntGroupVoicemailOptions(huntGroupId),
    },
    {
      queryKey: ['huntGroupVoicemailAdvanced', huntGroupId],
      queryFn: () => getHuntGroupVoicemailAdvanced(huntGroupId),
    },
  ]);
  const [updated, setUpdated] = useState(false);
  const [greetingList, setGreetingList] = useState([]);
  const greetingToRemove = useSelector(
    (state) => state.options.greetingToRemove,
  );
  const isVoiceMailActive = useSelector(
    (state) => state.options.isVoiceMailActive,
  );
  const announcements = useSelector((state) => state.options.announcements);
  const assignedAnnouncement = useSelector(
    (state) => state.options.assignedAnnouncement,
  );
  const [optionsIsDirty, setOptionsIsDirty] = useState(false);
  const [optionsHasErrors, setOptionsHasErrors] = useState(false);
  const optionsIsLoading = useSelector(
    (state) => state.options.optionsIsLoading,
  );
  const optionsFormRef = useSelector(
    (state) => state.formsState.optionsFormRef,
  );
  const optionsReset = useSelector(
    (state) => state.formsState.optionsFormReset,
  );
  const initOptionsFormState = useRef(null);
  // advanced form
  const [advancedIsDirty, setAdvancedIsDirty] = useState(false);
  const [advancedHasErrors, setAdvancedHasErrors] = useState(false);
  const advancedIsLoading = useSelector(
    (state) => state.advanced.advancedIsLoading,
  );
  const busyGreetingOptions = useSelector(
    (state) => state.advanced.busyGreetingOptions,
  );
  const advancedFormRef = useSelector(
    (state) => state.formsState.advancedFormRef,
  );
  const advancedReset = useSelector(
    (state) => state.formsState.advancedFormReset,
  );
  const initAdvancedFormState = useRef(null);
  // cancel and save
  const [showSaveCancel, setShowSaveCancel] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);

  const [showAddGreeting, setShowAddGreeting] = useState(false);
  const setSnackbar = useSnackbar();

  const {
    featureState: { isNewVoicemail },
  } = useContext(FeatureContext);

  const isNewGreetingRecordingShown = isNewVoicemail && showAddGreeting;

  useEffect(() => {
    if (showAddGreeting) window.scroll(0, 0);
  }, [showAddGreeting]);

  const getVoicemailOptions = (response) => {
    if (voiceMailOptionsError) {
      // TODO: throw up an error message
      dispatch(setOptionsIsLoading(false));
      return;
    }
    setExtension(response.extension);
    // voicemail vis
    dispatch(setIsVoiceMailActive(response.isVoiceMailActive));
    dispatch(setAnnouncements(response.announcements));
    dispatch(setAssignedAnnouncement(response.assignedAnnouncement));
    dispatch(setDefaultAnnouncement(response.defaultAnnouncement));
    // options form
    dispatch(setOptionsResponse(response));
    dispatch(setOptionsIsLoading(false));
    // set initial form state
    initOptionsFormState.current = { ...response, pinNumber: '••••' };
  };
  const getVocemailAdvanced = (response) => {
    if (voiceMailAdvancedError) {
      // TODO: throw up an error message
      dispatch(setAdvancedIsLoading(false));
      return;
    }
    // clean the response
    if (response.personalEmail === null) response.personalEmail = '';
    if (response.transferNumber === null) response.transferNumber = '';
    initAdvancedFormState.current = response;
    dispatch(setAdvancedResponse(response));
    dispatch(setAdvancedIsLoading(false));
  };

  useEffect(() => {
    // Clear busy greeting options when huntGroupId changes
    dispatch(clearBusyGreetingOptions());
  }, [huntGroupId, dispatch]);

  useEffect(() => {
    if (!huntGroupId || voiceMailOptionsLoading || voiceMailAdvancedLoading)
      return;
    getVoicemailOptions(voiceMailOptions);
    getVocemailAdvanced(voiceMailAdvanced);
  }, [
    huntGroupId,
    voiceMailOptionsLoading,
    voiceMailOptionsError,
    voiceMailAdvancedLoading,
    voiceMailAdvancedError,
  ]);

  useEffect(() => {
    if (!showSaveCancel && (optionsIsDirty || advancedIsDirty)) {
      setShowSaveCancel(true);
    }
    if (optionsIsDirty || advancedIsDirty) {
      setSaveDisabled(false);
    }
  }, [optionsIsDirty, advancedIsDirty]);

  useEffect(
    () => () => {
      if (updated) {
        removeVoiceMailOptions();
        removeVoiceMailAdvanced();
      }
    },
    [updated],
  );

  const uploadCallback = async (data, greetingName) => {
    const newGreeting = {
      greetingName,
      greetingType: 'WAV',
    };
    const formData = new FormData();
    formData.append('upload', data);

    try {
      await postHuntGroupVoicemailGreeting(
        huntGroupId,
        greetingName,
        formData,
        true,
      );
      await putHuntGroupVoicemailGreeting(huntGroupId, newGreeting);

      let greetings = [...announcements];
      const defaultIndex = greetings.findIndex(
        (g) => g.greetingName === 'Default Greeting',
      );
      greetings.splice(defaultIndex, 1);
      greetings = [newGreeting, defaultGreeting, ...greetings];

      setGreetingList(greetings);

      dispatch(setAnnouncements(greetings));
      dispatch(setAssignedAnnouncement(greetingName));
      dispatch(setBusyGreetingOptions([newGreeting, ...busyGreetingOptions]));
      setSnackbar({
        message: 'Success. Voicemail has been uploaded.',
        severity: 'success',
      });
      setUpdated(true);
    } catch (err) {
      setSnackbar({
        message: getVoicemailErrorMessage(err?.message),
        severity: 'error',
      });
    }
  };

  const errorCallback = () => {
    // TODO: error message
  };
  const nameChangeCallback = () => {
    // TODO: success message
  };

  const handleDeleteGreeting = (greetingNameToDelete) => {
    deleteHuntGroupVoicemailGreeting(huntGroupId, greetingNameToDelete)
      .then(() => {
        const indexToDelete = announcements.findIndex(
          (a) => a.greetingName === greetingNameToDelete,
        );
        const greetings = [...announcements];
        greetings.splice(indexToDelete, 1);

        dispatch(setGreetingToRemove(null));

        if (indexToDelete === 0) {
          dispatch(setAssignedAnnouncement(defaultGreeting.greetingName));
          putHuntGroupVoicemailGreeting(huntGroupId, defaultGreeting)
            .then(() => {
              // TODO: success message
            })
            .catch(() => {
              // TODO: failure message
            });
        }
        dispatch(setBusyGreetingOptions(greetings));
        dispatch(setAnnouncements(greetings));
        setSnackbar({
          message: 'Voicemail successfully deleted',
          severity: 'success',
        });
        setUpdated(true);
      })
      .catch(({ message }) => {
        setSnackbar({ message, severity: 'error' });
      });
  };

  const onCancel = () => {
    if (optionsIsDirty) {
      setSaveDisabled(true);
      dispatch(setPinNumber('••••'));
      optionsReset(initOptionsFormState.current);
    }
    if (advancedIsDirty) {
      if (initAdvancedFormState.current.storeOnNextivaSystem) {
        dispatch(setStorageType('NEXTIVA_STORE'));
      } else {
        dispatch(setStorageType('SEND_EMAIL'));
      }
      dispatch(setTransferOnZero(initAdvancedFormState.current.transferOn0));
      setSaveDisabled(true);
      advancedReset(initAdvancedFormState.current);
    }
    setShowSaveCancel(false);
    setOptionsIsDirty(false);
    setAdvancedIsDirty(false);
  };

  const onSave = () => {
    if (optionsHasErrors || advancedHasErrors) return;

    if (optionsIsDirty) {
      optionsFormRef.current.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true }),
      );
    }
    if (advancedIsDirty) {
      advancedFormRef.current.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true }),
      );
    }
  };

  const onOptionsSubmit = (data) => {
    setSaveDisabled(true);
    setShowSpinner(true);
    if (data.pinNumber === '••••' || data.pinNumber === '') {
      delete data.pinNumber;
    }

    if (!data.notificationEmailEnabled) {
      data.notificationEmail = '';
    }

    putHuntGroupVoicemailOptions(huntGroupId, data)
      .then(() => {
        data.pinNumber = '••••';
        setSnackbar(SUCCESS_SNACKBAR);
        setShowSpinner(false);
        setShowSaveCancel(false);
        optionsReset(data);
        initOptionsFormState.current = data;
        setUpdated(true);
      })
      .catch((err) => {
        setSnackbar({ message: err.message, severity: 'error' });
        setSaveDisabled(false);
        setShowSpinner(false);
      });
  };

  const onAdvancedSubmit = (data) => {
    setSaveDisabled(true);
    setShowSpinner(true);
    // if active is not set the backend will deactivate voicemail
    data.active = true;

    // if disableMessageDeposit and disconnectAfterGreeting are not set the service will
    // not be able to save due to the default values causing errors
    data.disableMessageDeposit =
      initAdvancedFormState.current.disableMessageDeposit;
    data.disconnectAfterGreeting =
      initAdvancedFormState.current.disconnectAfterGreeting;
    putHuntGroupVoicemailAdvanced(huntGroupId, data)
      .then(() => {
        setSnackbar(SUCCESS_SNACKBAR);
        setShowSpinner(false);
        setShowSaveCancel(false);
        advancedReset(data);
        initAdvancedFormState.current = data;
        setUpdated(true);
      })
      .catch((err) => {
        setSnackbar({ message: err.message, severity: 'error' });
        setSaveDisabled(false);
        setShowSpinner(false);
      });
  };

  const getVoicemailGreetings = () => {
    return announcements.map((announcement) => ({
      greetingName: announcement.greetingName,
      isActive: announcement.greetingName === assignedAnnouncement,
      disabledPlay: false,
      greetingFile: `/api/voice-settings-orchestration/hunt-groups/${huntGroupId}/announcements/${announcement.greetingName}.wav`,
      greetingType: announcement.greetingType,
    }));
  };

  const setActiveGreetingCallback = async (activeGreeting) => {
    let greetings = [...announcements];
    const currentActiveIndex = greetings.findIndex(
      (greeting) => greeting.greetingName === activeGreeting.greetingName,
    );
    greetings.splice(currentActiveIndex, 1);
    greetings = [activeGreeting, ...greetings];

    dispatch(setAssignedAnnouncement(activeGreeting.greetingName));
    setGreetingList(greetings);
    dispatch(setAnnouncements(greetings));
    await putHuntGroupVoicemailGreeting(huntGroupId, activeGreeting);
    setUpdated(true);
  };

  return (
    <>
      {(advancedIsLoading || optionsIsLoading) && <Skeleton />}
      {!advancedIsLoading && !optionsIsLoading && (
        <>
          <Container>
            <Header data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-VoiceMailTitle">
              Voicemail
              <Switch
                huntGroupId={huntGroupId}
                checked={isVoiceMailActive}
                setUpdated={setUpdated}
              />
            </Header>
            <Divider />
            {!isNewGreetingRecordingShown && (
              <Info data-testid="PlatformAdminUI-TeamVoiceSettings-VoiceMail-VoiceMailDescription">
                You can access this voicemail box by dialing <b>*86</b> from a
                Nextiva Phone. When connected, press <b>star (*)</b>. When
                prompted, enter the extension number <b>{extension}</b>, and the
                PIN which can be updated below.
              </Info>
            )}
            {isNewVoicemail ? (
              <GreetingVisualizer
                uploadCallback={uploadCallback}
                showAddGreeting={showAddGreeting}
                addGreetingHandler={() => {
                  setShowAddGreeting(!showAddGreeting);
                }}
                errorCallback={errorCallback}
                nameChangeCallback={nameChangeCallback}
                greetingList={getVoicemailGreetings()}
                setActiveGreetingCallback={setActiveGreetingCallback}
                deleteGreetingCallBack={handleDeleteGreeting}
              />
            ) : (
              <VoicemailVisualizer
                announcements={announcements}
                uploadCallback={uploadCallback}
                errorCallback={errorCallback}
                nameChangeCallback={nameChangeCallback}
                greetingList={greetingList}
              />
            )}

            {!isNewGreetingRecordingShown && (
              <>
                <OptionsForm
                  setOptionsIsDirty={setOptionsIsDirty}
                  setOptionsHasErrors={setOptionsHasErrors}
                  onSubmit={onOptionsSubmit}
                  isVoicemailActive={isVoiceMailActive}
                  huntGroupId={huntGroupId}
                  data-testid="PlatformAdminUI-Voicemail-OptionsForm"
                />
                <RevealCard>
                  <AdvancedForm
                    setAdvancedIsDirty={setAdvancedIsDirty}
                    setAdvancedHasErrors={setAdvancedHasErrors}
                    onSubmit={onAdvancedSubmit}
                    isVoicemailActive={isVoiceMailActive}
                    isHipaaEnabled={isHipaaEnabled}
                    data-testid="PlatformAdminUI-Voicemail-AdvancedForm"
                  />
                </RevealCard>
              </>
            )}
          </Container>
          <SectionSaveCancel
            onCancel={onCancel}
            onSave={onSave}
            open={showSaveCancel}
            saveDisabled={saveDisabled}
            showSpinner={showSpinner}
          />
        </>
      )}
      <NotifyModal
        cancelText="Cancel"
        confirmText="Delete"
        mode={DIALOG_KINDS.ERROR}
        onCancel={() => dispatch(setGreetingToRemove(null))}
        onConfirm={() => {
          handleDeleteGreeting(greetingToRemove.greetingName);
        }}
        open={!!greetingToRemove}
        title="Delete Voicemail Greeting?"
      >
        <div className="m-notify-modal">
          This action will permanently delete the below voicemail greeting from
          your account.
        </div>
        <div className="m-notify-modal m-notify-modal__main--bold">
          {greetingToRemove?.greetingName}
        </div>
      </NotifyModal>
    </>
  );
};

Voicemail.propTypes = {
  huntGroupId: string,
  isHipaaEnabled: bool,
};

export default Voicemail;
